import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInfo } from '@c2fo/liquidity';
import download from '@/enterprise/assets/download.svg?url';
import downloadError from '@/enterprise/assets/downloaderror.svg?url';
import useAwardFileStatus from '@/enterprise/data/useAwardFileStatus';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import getToken from '@/utils/token';

const prefixUrl = import.meta.env.VITE_GATEWAY_URL;

const InvoiceFileDownloadModal = ({ jobUuid, isUrlExpired }: { jobUuid: string; isUrlExpired: boolean }) => {
  const token = getToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(true);

  const params = qs.stringify({ jobUuid, token });
  const downloadUrl = `${prefixUrl}/usercast/api/c2fo/taker/invoices/eligible/csv?${params}`;

  const onFinish = () => {
    setShowModal(false);
    navigate(asEnterpriseLink('markets'));
  };

  // Show expired modal if URL expired
  if (isUrlExpired) {
    return (
      <Modal onClose={onFinish} open={showModal} className="sm:w-[550px]">
        <ModalTitleInfo>{t('core.authenticatedDownload.expiredTitle')}</ModalTitleInfo>
        <ModalContent className="flex flex-col items-center gap-6 text-center">
          <img src={downloadError} alt="download error" className="h-16 w-16" />
          <div>{t('core.authenticatedDownload.expired')}</div>
        </ModalContent>
        <ModalActions className="sm:justify-center">
          <Button variant="primary" onClick={onFinish} className="sm:w-1/2">
            {t('core.close')}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <Modal open={showModal} onClose={onFinish} size="sm">
      <ModalTitleInfo>{t('core.authenticatedDownload.title')}</ModalTitleInfo>
      <ModalContent center>
        <img src={download} alt="download" className="h-16 w-16" />
        <p className="text-center text-base">{t('core.authenticatedDownload.description')}</p>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={onFinish}>
          {t('core.cancel')}
        </Button>
        <a className="shrink" href={downloadUrl} onClick={onFinish} target="_self">
          <Button variant="primary">{t('core.download')}</Button>
        </a>
      </ModalActions>
    </Modal>
  );
};

// Award file downloads with file status check
const AwardFileDownloadModal = ({
  startDate,
  endDate,
  makerIds,
  isUrlExpired,
}: {
  startDate: string;
  endDate: string;
  makerIds: string[];
  isUrlExpired: boolean;
}) => {
  const token = getToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(true);

  // Check if file is complete or errored
  const { data: fileStatus } = useAwardFileStatus({
    startDate,
    endDate,
    makerIds,
  });

  const params = qs.stringify({ startDate, endDate, makerIds, token }, { indices: false });
  const downloadUrl = `${prefixUrl}/usercast/api/c2fo/taker/consolidated-history/award/archive-by-deposit-date?${params}`;

  const onFinish = () => {
    const cancelParams = qs.stringify({ startDate, endDate, makerIds }, { indices: false });
    setShowModal(false);
    navigate(asEnterpriseLink(`history?${cancelParams}`));
  };

  // Show expired modal if URL expired or file not available
  if (isUrlExpired || !fileStatus?.isComplete || fileStatus?.isError) {
    return (
      <Modal onClose={onFinish} open={showModal} className="sm:w-[550px]">
        <ModalTitleInfo>{t('core.authenticatedDownload.expiredTitle')}</ModalTitleInfo>
        <ModalContent className="flex flex-col items-center gap-6 text-center">
          <img src={downloadError} alt="download error" className="h-16 w-16" />
          <div>{t('core.authenticatedDownload.expired')}</div>
        </ModalContent>
        <ModalActions className="sm:justify-center">
          <Button variant="primary" onClick={onFinish} className="sm:w-1/2">
            {t('core.close')}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  // Show download modal
  return (
    <Modal open={showModal} onClose={onFinish} size="sm">
      <ModalTitleInfo>{t('core.authenticatedDownload.title')}</ModalTitleInfo>
      <ModalContent center>
        <img src={download} alt="download" className="h-16 w-16" />
        <p className="text-center text-base">{t('core.authenticatedDownload.description')}</p>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={onFinish}>
          {t('core.cancel')}
        </Button>
        <a className="shrink" href={downloadUrl} onClick={onFinish} target="_self">
          <Button variant="primary">{t('core.download')}</Button>
        </a>
      </ModalActions>
    </Modal>
  );
};

const FileDownloadModal = () => {
  const [searchParams] = useSearchParams();

  const downloadConsolidatedInvoice = searchParams.get('downloadConsolidatedInvoice');
  const downloadConsolidatedTakerAward = searchParams.get('downloadConsolidatedTakerAward');
  const startDate = searchParams.get('startDate') ?? '';
  const endDate = searchParams.get('endDate') ?? '';
  const makerIds = searchParams.getAll('makerIds');

  const currentDate = new Date();
  const expiration = new Date(searchParams.get('urlExpirationDate') ?? currentDate);
  const isUrlExpired = expiration < currentDate;

  // Route to the correct modal type
  if (downloadConsolidatedInvoice) {
    return <InvoiceFileDownloadModal jobUuid={downloadConsolidatedInvoice} isUrlExpired={isUrlExpired} />;
  }

  if (downloadConsolidatedTakerAward && startDate) {
    return (
      <AwardFileDownloadModal startDate={startDate} endDate={endDate} makerIds={makerIds} isUrlExpired={isUrlExpired} />
    );
  }

  return null;
};

export default FileDownloadModal;
